"use client";

import type { ReactNode } from "react";

/**
 * Full-viewport ghost of the app layout with a centered error card overlay.
 * Uses ONLY inline styles — no Tailwind, no providers — so it works in both
 * route-level error.tsx (where Tailwind exists) and global-error.tsx (where
 * the entire DOM is replaced and Tailwind is unavailable).
 *
 * The `children` slot replaces the error card content (icon, title, buttons),
 * allowing each consumer to provide translated or hardcoded strings.
 */

/** Ghost table row with varied column widths. */
function GhostRow({ seed }: { seed: number }) {
  const widths = [
    55 + (seed % 3) * 15,
    45 + (seed % 4) * 10,
    60 + (seed % 2) * 20,
    40 + (seed % 3) * 15,
    35 + (seed % 5) * 10,
  ];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: "1rem",
        padding: "0.875rem 1.25rem",
        borderBottom: "1px solid rgba(0,0,0,0.02)",
      }}
    >
      {widths.map((w, i) => (
        <div
          key={i}
          style={{
            height: "10px",
            width: `${w}%`,
            borderRadius: "4px",
            backgroundColor: "rgba(0,0,0,0.03)",
          }}
        />
      ))}
    </div>
  );
}

/**
 * Full-bleed ghost app layout: sidebar (md+) + header + dashboard content,
 * with a frosted error card centered on top.
 *
 * @param children - Content inside the error card (title, description, buttons)
 * @param hideSidebar - When true, only renders the ghost header + content (for
 *   use inside the protected layout where the real sidebar is already visible)
 */
export function GhostErrorLayout({
  children,
  hideSidebar = false,
}: {
  children: ReactNode;
  hideSidebar?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: "#fafafa",
        color: "#1a1a1a",
      }}
    >
      {/* ── Ghost sidebar (hidden mobile, visible md+) ── */}
      {!hideSidebar && (
        <>
          <style>
            {
              "@media (min-width: 768px) { .ge-sidebar { display: flex !important; } }"
            }
          </style>
          <div
            className="ge-sidebar"
            style={{
              width: "260px",
              minHeight: "100vh",
              background:
                "linear-gradient(180deg, rgba(45,62,143,0.06) 0%, rgba(30,30,60,0.04) 100%)",
              borderRight: "1px solid rgba(0,0,0,0.04)",
              flexShrink: 0,
              display: "none",
              flexDirection: "column",
            }}
          >
            {/* Logo */}
            <div
              style={{
                padding: "1.5rem",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "140px",
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: "rgba(0,0,0,0.04)",
                }}
              />
            </div>
            {/* Nav items */}
            <div
              style={{
                padding: "0 1rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem",
              }}
            >
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  style={{
                    height: "36px",
                    borderRadius: "6px",
                    backgroundColor:
                      i === 1 ? "rgba(0,0,0,0.05)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 0.75rem",
                    gap: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "4px",
                      backgroundColor: "rgba(0,0,0,0.06)",
                      flexShrink: 0,
                    }}
                  />
                  <div
                    style={{
                      width: `${40 + i * 8}%`,
                      height: "10px",
                      borderRadius: "4px",
                      backgroundColor: "rgba(0,0,0,0.05)",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Main content area ── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          position: "relative",
        }}
      >
        {/* Ghost header */}
        <div
          style={{
            height: "56px",
            borderBottom: "1px solid rgba(0,0,0,0.05)",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            padding: "0 1.5rem",
            gap: "0.75rem",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: "20%",
              maxWidth: "80px",
              height: "10px",
              borderRadius: "4px",
              backgroundColor: "rgba(0,0,0,0.05)",
            }}
          />
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "rgba(0,0,0,0.08)",
            }}
          />
          <div
            style={{
              width: "15%",
              maxWidth: "60px",
              height: "10px",
              borderRadius: "4px",
              backgroundColor: "rgba(0,0,0,0.04)",
            }}
          />
        </div>

        {/* Ghost dashboard content (behind error overlay) */}
        <div
          style={{
            position: "absolute",
            top: "56px",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "1.5rem",
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          {/* Title + action */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{
                width: "20%",
                minWidth: "100px",
                height: "24px",
                borderRadius: "4px",
                backgroundColor: "rgba(0,0,0,0.04)",
              }}
            />
            <div
              style={{
                width: "90px",
                height: "32px",
                borderRadius: "6px",
                backgroundColor: "rgba(0,0,0,0.03)",
              }}
            />
          </div>

          {/* Summary cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  padding: "1.25rem",
                  borderRadius: "12px",
                  border: "1px solid rgba(0,0,0,0.04)",
                  backgroundColor: "rgba(255,255,255,0.4)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    width: "40%",
                    height: "10px",
                    borderRadius: "4px",
                    backgroundColor: "rgba(0,0,0,0.04)",
                  }}
                />
                <div
                  style={{
                    width: "60%",
                    height: "18px",
                    borderRadius: "4px",
                    backgroundColor: "rgba(0,0,0,0.03)",
                  }}
                />
                <div
                  style={{
                    width: "30%",
                    height: "10px",
                    borderRadius: "4px",
                    backgroundColor: "rgba(0,0,0,0.02)",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Data table */}
          <div
            style={{
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.04)",
              backgroundColor: "rgba(255,255,255,0.4)",
              overflow: "hidden",
            }}
          >
            {/* Header row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "1rem",
                padding: "0.75rem 1.25rem",
                borderBottom: "1px solid rgba(0,0,0,0.04)",
              }}
            >
              {[75, 60, 80, 50, 40].map((w, i) => (
                <div
                  key={i}
                  style={{
                    height: "10px",
                    width: `${w}%`,
                    borderRadius: "4px",
                    backgroundColor: "rgba(0,0,0,0.04)",
                  }}
                />
              ))}
            </div>
            {[1, 2, 3, 4, 5, 6, 7].map((row) => (
              <GhostRow key={row} seed={row} />
            ))}
          </div>
        </div>

        {/* ── Error card (centered overlay) ── */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem 1.5rem",
            position: "relative",
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: "28rem",
              width: "100%",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1.25rem",
              padding: "2rem",
              borderRadius: "1rem",
              border: "1px solid rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {/* Error icon */}
            <div
              style={{
                width: "3rem",
                height: "3rem",
                borderRadius: "50%",
                backgroundColor: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dc2626"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            </div>
            {}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
